import {
  createReviewMenuItems,
  createValidationMenuItems,
} from "@platform/plan-grid-core";

import {
  resolvePlanRateReviewTargets,
  resolveRateReviewTarget,
} from "../../utils/rateReviewTarget";

import type {
  CellReview,
  ReviewMenuCallbacks,
  ValidationMenuCallbacks,
  ValidationMenuLabels,
} from "@platform/plan-grid-core";
import type { DefaultMenuItem, MenuItemDef } from "ag-grid-community";

export interface RateReviewMenuCallbacks extends ReviewMenuCallbacks<CellReview> {
  getReview: (planId: string, fieldPath: string) => CellReview | undefined;
}

/**
 * Builds the review items for a right-click inside the rates grid.
 *
 * The grid holds one AG Grid cell per plan and draws the whole rate table
 * inside it, so the cell's identity comes from the event target rather than
 * from the column and row. The items themselves are the shared ones.
 *
 * `validation` adds the validated-flag items for the same cell; omitting it
 * leaves the menu to the review items alone.
 *
 * Returns nothing when the click missed a reviewable rate cell, which leaves
 * AG Grid showing its own default menu.
 */
export const createRateReviewMenuItems = (
  eventTarget: EventTarget | null,
  callbacks: RateReviewMenuCallbacks,
  validation?: ValidationMenuCallbacks,
): (DefaultMenuItem | MenuItemDef)[] => {
  const target = resolveRateReviewTarget(eventTarget);

  if (!target) {
    return [];
  }

  const reviewItems = createReviewMenuItems(
    target,
    callbacks.getReview(target.planId, target.fieldPath),
    callbacks,
  );

  return [
    ...reviewItems,
    ...(validation ? createValidationMenuItems([target], validation) : []),
  ];
};

const PLAN_VALIDATION_LABELS: ValidationMenuLabels = {
  validate: "Validate Rate Plan",
  clear: "Clear Rate Plan Validation",
};

export const createPlanRateValidationMenuItems = (
  eventTarget: EventTarget | null,
  validation: ValidationMenuCallbacks,
): (DefaultMenuItem | MenuItemDef)[] => {
  const targets = resolvePlanRateReviewTargets(eventTarget);

  return targets.length > 0
    ? createValidationMenuItems(targets, validation, PLAN_VALIDATION_LABELS)
    : [];
};
